/**
 * PasskeyManager.jsx — Profile-page section to enrol & manage passkeys.
 *
 * Lists existing passkeys with creation date + last-used + delete, and an
 * "Add a passkey" button that runs the WebAuthn registration ceremony.
 */
import React, { useEffect, useState } from 'react';
import { KeyRound, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  enrollPasskey,
  listPasskeys,
  deletePasskey,
  isPasskeySupported,
} from '../utils/passkeyApi';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

const detectDefaultName = () => {
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad/.test(ua)) return 'iPhone (Face/Touch ID)';
  if (/Mac/.test(ua)) return 'Mac (Touch ID)';
  if (/Android/.test(ua)) return 'Android device';
  if (/Windows/.test(ua)) return 'Windows (Hello)';
  return 'Passkey';
};

const PasskeyManager = () => {
  const [passkeys, setPasskeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const d = await listPasskeys();
      setPasskeys(d.passkeys || []);
    } catch {
      // Silent — list call is best-effort
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const handleEnrol = async () => {
    if (enrolling) return;
    const suggested = detectDefaultName();
    // eslint-disable-next-line no-alert
    const nickname = window.prompt('Name this passkey (helps you tell devices apart):', suggested);
    if (nickname === null) return;
    setEnrolling(true);
    const t = toast.loading('Waiting for biometric / device unlock...');
    try {
      const out = await enrollPasskey(nickname.trim() || suggested);
      toast.success(`✅ Passkey "${out.name}" enrolled.`, { id: t });
      try { window.dispatchEvent(new CustomEvent('passkey:changed')); } catch { /* ignore */ }
      refresh();
    } catch (e) {
      // User cancellation surfaces as 'NotAllowedError' — friendlier copy.
      const msg = /NotAllowedError|cancell/i.test(e.message || '')
        ? 'Cancelled.'
        : (e.message || 'Enrolment failed');
      toast.error(msg, { id: t });
    } finally {
      setEnrolling(false);
    }
  };

  const handleDelete = async (passkey) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove passkey "${passkey.name}"? You'll need your password (or another passkey) to sign in from this device.`)) return;
    const t = toast.loading('Removing passkey...');
    try {
      await deletePasskey(passkey.credential_id);
      toast.success('Passkey removed.', { id: t });
      try { window.dispatchEvent(new CustomEvent('passkey:changed')); } catch { /* ignore */ }
      refresh();
    } catch (e) {
      toast.error(e.message || 'Delete failed', { id: t });
    }
  };

  if (!isPasskeySupported()) {
    return (
      <Card className="bg-slate-800/50 border-slate-700" data-testid="passkey-manager">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2 text-base">
            <KeyRound className="w-4 h-4 text-fuchsia-300" /> Passkeys
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-gray-400">
            Your browser doesn&apos;t support passkeys. Try Safari, Chrome, or Firefox on a modern device.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-slate-800/50 border-slate-700" data-testid="passkey-manager">
      <CardHeader className="flex-row items-center justify-between space-y-0 flex-wrap gap-2">
        <CardTitle className="text-white flex items-center gap-2 text-base">
          <KeyRound className="w-4 h-4 text-fuchsia-300" />
          Passkeys
          <span className="text-[10px] uppercase tracking-wider text-gray-500 font-normal">passwordless sign-in</span>
        </CardTitle>
        <Button
          size="sm"
          onClick={handleEnrol}
          disabled={enrolling}
          className="bg-fuchsia-600 hover:bg-fuchsia-700 h-8 px-3 text-xs"
          data-testid="passkey-add-btn"
        >
          <Plus className="w-3.5 h-3.5 mr-1" />
          {enrolling ? 'Enrolling...' : 'Add a passkey'}
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-xs text-gray-500">Loading...</p>
        ) : passkeys.length === 0 ? (
          <p className="text-xs text-gray-400" data-testid="passkey-empty-state">
            No passkeys enrolled yet. Adding one lets you sign in with Face ID / Touch ID / Windows Hello — no password required next time.
          </p>
        ) : (
          <ul className="space-y-1.5">
            {passkeys.map((pk) => (
              <li
                key={pk.credential_id}
                className="flex items-center justify-between gap-3 p-2.5 bg-slate-900/40 rounded-md border border-slate-700/50"
                data-testid={`passkey-row-${pk.credential_id.slice(0, 12)}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white font-medium truncate">{pk.name}</p>
                  <p className="text-[10px] text-gray-500">
                    Added {pk.created_at ? new Date(pk.created_at).toLocaleDateString() : '—'}
                    {pk.last_used_at && ` · last used ${new Date(pk.last_used_at).toLocaleDateString()}`}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleDelete(pk)}
                  className="text-red-400 hover:text-red-300 p-1.5 rounded hover:bg-red-500/10"
                  aria-label={`Remove ${pk.name}`}
                  data-testid={`passkey-delete-${pk.credential_id.slice(0, 12)}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

export default PasskeyManager;
